import React from "react";
import { Drawer } from "flowbite-react";
import { IconType } from "react-icons";
import { useContentScrollbarWidth } from "../../hooks/useContentScrollbarWidth";

interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  icon?: IconType;
  titleAction?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  widthClassName?: string;
}

const NoIcon = () => null;

export const SideDrawer: React.FC<SideDrawerProps> = ({
  open,
  onClose,
  title,
  icon,
  titleAction,
  children,
  footer,
  widthClassName = "w-[calc(100%-var(--side-drawer-offset))] max-w-2xl",
}) => {
  const scrollbarWidth = useContentScrollbarWidth(open);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      position="right"
      className={widthClassName}
      aria-hidden={!open}
      // A closed drawer only slides off screen: without inert, Tab and screen readers still reach its fields.
      {...(open ? {} : { inert: "" })}
      style={
        {
          right: scrollbarWidth,
          "--side-drawer-offset": `${scrollbarWidth}px`,
          height: "calc(100dvh - 4rem)",
          paddingBottom: "calc(1rem + env(safe-area-inset-bottom))",
        } as React.CSSProperties
      }
      theme={{
        root: {
          base: "fixed z-40 flex flex-col bg-white p-4 transition-transform",
          position: {
            right: {
              on: "top-16 h-[calc(100vh-4rem)] transform-none",
              off: "top-16 h-[calc(100vh-4rem)] translate-x-full",
            },
          },
        },
      }}
    >
      {title !== undefined &&
        (titleAction ? (
          <Drawer.Header
            title={title}
            titleIcon={icon ?? NoIcon}
            className="flex items-start"
            theme={{
              collapsed: { on: "flex items-center", off: "flex items-center" },
            }}
          >
            {titleAction}
          </Drawer.Header>
        ) : (
          <Drawer.Header title={title} titleIcon={icon ?? NoIcon} />
        ))}
      <Drawer.Items className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y pr-4">
        {children}
      </Drawer.Items>
      {footer}
    </Drawer>
  );
};

export default SideDrawer;
